import type { SupplierDocumentPipelineStatus } from '@/services/supplierDocumentPipelineStatusService';
import { formatUkDateTime } from '@/utils/ukDates';

/**
 * Which part of the supplier-documents pipeline is behind, and since when, for the top-bar chip and its tooltip.
 *
 * The chip used to say only "Supplier docs degraded". The database sets that level from the same conditions used here
 * (supabase/migrations/20261010310000_supplier_pipeline_status_live_overdue_only.sql), so each condition that can
 * degrade it is named in words: invoice reading, ticket PDFs, or the live Sabre / Amadeus checks. "Since" is the last
 * time that part finished a good run, because the status carries no timestamp for when it first slipped.
 * E-tickets to review are a work list, not a fault, and are never named here.
 */
export interface SupplierDocsBehindPart {
  key: 'invoices' | 'tickets' | 'live_checks' | 'supplier_sync';
  /** Short, for the chip: "live PNR checks behind". */
  chip: string;
  /** A full sentence for the tooltip and the details panel. */
  detail: string;
  /** When that part last finished a good run; null when it never has or the status does not say. */
  since: string | null;
}

const plural = (n: number, one: string, many: string) => `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`;

/** A run that started this long ago and has not run since is "behind": the database uses 35 minutes for the two Gmail pollers. */
const POLLER_STALE_MINUTES = 35;

const minutesBetween = (earlier: string | null | undefined, later: string): number | null => {
  if (!earlier) return null;
  const a = Date.parse(earlier);
  const b = Date.parse(later);
  return Number.isFinite(a) && Number.isFinite(b) ? Math.floor((b - a) / 60000) : null;
};

export function supplierDocsBehind(pipeline: SupplierDocumentPipelineStatus | null | undefined): SupplierDocsBehindPart[] {
  if (!pipeline || pipeline.status === 'healthy' || pipeline.status === 'unknown') return [];
  const parts: SupplierDocsBehindPart[] = [];
  const idleFor = (run: { last_run_at?: string | null }) => minutesBetween(run.last_run_at, pipeline.generated_at);

  const invoice = pipeline.invoice_ingestion;
  const invoiceProblems: string[] = [];
  const invoiceIdle = idleFor(invoice);
  if (invoice.last_status === 'failed') invoiceProblems.push('the last run failed');
  else if (invoice.last_status === 'partial') invoiceProblems.push('the last run only partly finished');
  if (invoiceIdle !== null && invoiceIdle > POLLER_STALE_MINUTES) invoiceProblems.push(`it has not run for ${invoiceIdle} minutes`);
  if (invoice.pending > 10) invoiceProblems.push(`${plural(invoice.pending, 'invoice is', 'invoices are')} waiting to be read`);
  if (invoice.parse_errors > 0) invoiceProblems.push(`${plural(invoice.parse_errors, 'invoice', 'invoices')} could not be read`);
  if (invoice.invalid_ticket_values > 0) invoiceProblems.push(`${plural(invoice.invalid_ticket_values, 'ticket value was', 'ticket values were')} rejected`);
  if (invoiceProblems.length > 0) {
    parts.push({
      key: 'invoices',
      chip: 'invoice reading behind',
      detail: `Invoice reading from Gmail: ${invoiceProblems.join('; ')}.`,
      since: invoice.last_completed_at ?? null,
    });
  }

  const ticket = pipeline.ticket_ingestion;
  const ticketProblems: string[] = [];
  const ticketIdle = idleFor(ticket);
  if (ticket.last_status === 'failed') ticketProblems.push('the last run failed');
  else if (ticket.last_status === 'partial') ticketProblems.push('the last run only partly finished');
  if (ticketIdle !== null && ticketIdle > POLLER_STALE_MINUTES) ticketProblems.push(`it has not run for ${ticketIdle} minutes`);
  if (ticket.failed > 0) ticketProblems.push(`${plural(ticket.failed, 'ticket PDF', 'ticket PDFs')} failed`);
  if (ticketProblems.length > 0) {
    parts.push({
      key: 'tickets',
      chip: 'ticket PDFs failing',
      detail: `Ticket PDFs: ${ticketProblems.join('; ')}.`,
      since: ticket.last_completed_at ?? null,
    });
  }

  const gds = pipeline.gds_verification;
  const gdsProblems: string[] = [];
  if (gds.read_outage) gdsProblems.push('no live read has succeeded for 24 hours, which may be a Sabre or Amadeus outage');
  if ((gds.newly_unreadable_24h ?? 0) > 10) gdsProblems.push(`${plural(gds.newly_unreadable_24h ?? 0, 'booking', 'bookings')} became unreadable in the last 24 hours`);
  if (gds.overdue_candidates > 25) gdsProblems.push(`${plural(gds.overdue_candidates, 'current booking is', 'current bookings are')} overdue for its live check`);
  const gdsStaleAfter = (pipeline.schedules?.gds_minutes ?? 30) * 1.5;
  const gdsIdle = idleFor(gds);
  if (gds.last_status === 'failed') gdsProblems.push('the last run failed');
  else if (gds.last_status === 'partial') gdsProblems.push('the last run only partly finished');
  if (gdsIdle !== null && gdsIdle > gdsStaleAfter) gdsProblems.push(`it has not run for ${gdsIdle} minutes`);
  if (gdsProblems.length > 0) {
    parts.push({
      key: 'live_checks',
      chip: 'live PNR checks behind',
      detail: `Live Sabre / Amadeus checks: ${gdsProblems.join('; ')}.`,
      since: gds.last_successful_read_at ?? gds.last_completed_at ?? null,
    });
  }

  const sync = pipeline.supplier_sync;
  if (sync && (sync.last_status === 'failed' || sync.last_status === 'partial')) {
    parts.push({
      key: 'supplier_sync',
      chip: 'supplier sync failing',
      detail: `Linking supplier documents to bookings: the last run ${sync.last_status === 'failed' ? 'failed' : 'only partly finished'}.`,
      since: sync.last_completed_at ?? null,
    });
  }
  return parts;
}

/** The chip's summary after "Supplier docs:", or null to keep the plain level word. */
export function supplierDocsChipSummary(parts: SupplierDocsBehindPart[]): string | null {
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0].chip;
  if (parts.length === 2) return `${parts[0].chip} + ${parts[1].chip}`;
  return `${parts.length} parts behind`;
}

/** The sentence for the tooltip and the details panel, with the last good run in UK time. */
export function supplierDocsBehindSentence(parts: SupplierDocsBehindPart[]): string | null {
  if (parts.length === 0) return null;
  return parts
    .map((part) => `${part.detail} ${part.since ? `Last good run: ${formatUkDateTime(part.since)} UK time.` : 'No good run on record.'}`)
    .join(' ');
}
